import { backdropClasses, Theme } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { deepmerge } from "@mui/utils";
import { defaultTheme } from "react-admin";

export const primaryColor = "#002FA7";

const muiInputComponents = {
  MuiAutocomplete: {
    defaultProps: {
      variant: "outlined",
      size: "small",
    },
    styleOverrides: {
      root: {
        "& .MuiIconButton-root": {
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: "transparent",
          },
        },
      },
      noOptions: {
        display: "none",
      },
    },
  },

  MuiFormControl: {
    defaultProps: {
      size: "small",
      variant: "outlined",
    },
    styleOverrides: {
      root: {
        marginTop: 0,
        backgroundColor: "white",
        marginBottom: 0,
      },
    },
  },

  MuiInputBase: {
    defaultProps: {
      variant: "outlined",
      size: "small",
    },
  },

  MuiTextField: {
    defaultProps: {
      variant: "outlined",
      size: "small",
    },
  },
};

const muiDialogComponents = {
  MuiDialog: {
    defaultProps: {
      fullWidth: true,
      maxWidth: "sm",
    },
    styleOverrides: {
      root: () => ({
        "& .MuiCardContent-root": {
          // padding: 0,

          "& .MuiStack-root": {
            // paddingLeft: 0,
            // paddingRight: 0,
          },
        },
        "& .MuiToolbar-root": {
          paddingLeft: 0,
          paddingRight: 0,
        },
        "& .RaCreate-noActions": {
          marginTop: "0 !important",
        },
      }),
      paper: ({ theme }: { theme: Theme }) => ({
        [theme.breakpoints.down("sm")]: {
          margin: theme.spacing(1),
          width: `calc(100% - ${theme.spacing(2)})`,
          maxWidth: "100%",
        },
      }),
    },
  },
  // Potential fix for drawer problem having backdrop still open after closing
  // https://github.com/mui/material-ui/issues/32286
  MuiDrawer: {
    styleOverrides: {
      root: {
        [`&:has(> div.${backdropClasses.root}[style*="opacity: 0"])`]: {
          pointerEvents: "none",
        },
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        padding: `${theme.spacing(1)} ${theme.spacing(5)} ${theme.spacing(3)}`,
        [theme.breakpoints.down("sm")]: {
          padding: theme.spacing(2),
        },
      }),
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        padding: theme.spacing(5),
        paddingBottom: theme.spacing(3),
        paddingTop: theme.spacing(3) + " !important",
        [theme.breakpoints.down("sm")]: {
          padding: `${theme.spacing(2)} !important`,
        },

        "& .MuiToolbar-root": {
          justifyContent: "end",
        },
      }),
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        padding: `${theme.spacing(3)} ${theme.spacing(5)} 0`,
        [theme.breakpoints.down("sm")]: {
          padding: theme.spacing(2),
        },
      }),
    },
  },
};

const muiComponents = {
  ...muiInputComponents,
  ...muiDialogComponents,

  MuiCssBaseline: {
    styleOverrides: {
      "@font-face": {
        fontFamily: "Roboto",
        fontStyle: "normal",
        fontWeight: 400,
        fontDisplay: "swap",
        src: 'url("./fonts/Roboto-Regular.ttf") format("truetype")',
      },
      html: {
        width: "100%",
        height: "100%",
      },
      body: {
        width: "100%",
        height: "100%",
        margin: 0,
        wordWrap: "break-word",
      },
      code: {
        fontFamily: 'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace',
      },
      fallbacks: [
        {
          "@font-face": {
            fontFamily: "Roboto",
            fontStyle: "normal",
            fontWeight: 500,
            fontDisplay: "swap",
            src: 'url("./fonts/Roboto-Medium.ttf") format("truetype")',
          },
        },
        {
          "@font-face": {
            fontFamily: "Roboto",
            fontStyle: "normal",
            fontWeight: 700,
            fontDisplay: "swap",
            src: 'url("./fonts/Roboto-Bold.ttf") format("truetype")',
          },
        },
      ],
    },
  },

  MuiAppBar: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        boxShadow: "none",
        zIndex: 100,
        top: 0,
        color: theme.palette.primary.main,
        backgroundColor: "transparent",
      }),
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        fontSize: "1rem",
      },
      standardInfo: {
        backgroundColor: "#E0E4F0",
        color: primaryColor,
        "& .MuiAlert-icon": {
          color: primaryColor,
        },
      },
    },
  },
  MuiButton: {
    defaultProps: {
      size: "small",
      color: "info",
    },
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        textTransform: "none",
        padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
      }),

      // variants
      containedPrimary: {
        boxShadow: "none",
        "&:hover": {
          boxShadow: "none",
        },
      },
      containedInfo: ({ theme }: { theme: Theme }) => ({
        color: theme.palette.primary.main,
        boxShadow: "none",
        "&:hover": {
          boxShadow: "none",
        },
      }),
      containedError: {
        boxShadow: "none",
        "&:hover": {
          boxShadow: "none",
        },
      },
      textInfo: ({ theme }: { theme: Theme }) => ({
        color: theme.palette.primary.main,
        "&:hover": {
          backgroundColor: theme.palette.info.main,
        },
      }),

      // sizes
      sizeSmall: {
        // keeps buttons with and without a start icon at the same height
        minHeight: "36px",
        lineHeight: 1.5,
        padding: "6px 16px",
        fontSize: "14px",

        svg: {
          fontSize: "24px !important",
        },
      },
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        borderRadius: 4,
        padding: 18,
        fontSize: "1rem",
        width: "1.5rem",
        height: "1.5rem",

        backgroundColor: theme.palette.info.main,
        "&:hover": {
          backgroundColor: theme.palette.info.dark,
        },

        // not sure why this is needed, but otherwise mobile button svgs are not displayed
        "& svg": {
          flexShrink: 0,
        },
      }),
      colorSecondary: ({ theme }: { theme: Theme }) => ({
        backgroundColor: "transparent",
        color: theme.palette.primary.main,
        "&:hover": {
          backgroundColor: theme.palette.info.main,
        },
      }),
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        margin: "2px 2px !important",
        height: 24,
      },
      deleteIcon: {
        width: 16,
        height: 16,
      },
    },
  },

  MuiLink: {
    styleOverrides: {
      root: {
        textDecoration: "none",

        "&:hover": {
          textDecoration: "underline",
        },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: "none",
        fontSize: 16,
        fontWeight: 500,
        color: "rgba(0,0,0,0.87)",
        opacity: 1,
        minWidth: "160px",
        "&.Mui-selected": {
          color: "rgba(0,0,0,0.87)",
        },
      },
    },
  },

  MuiTabScrollButton: {
    styleOverrides: {
      root: {
        "&.Mui-disabled": {
          width: 0,
          transition: "width 0.25s linear",
        },
      },
    },
  },

  MuiTablePagination: {
    styleOverrides: {
      select: {
        paddingBottom: "2px",
      },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: {
        "&.MuiTableCell-paddingCheckbox": {
          padding: "0px 0 0 8px",
        },
      },
    },
  },

  MuiTableRow: {
    styleOverrides: {
      root: {
        "&:last-child td": {
          borderBottom: "none",
        },
      },
    },
  },

  MuiToolbar: {
    styleOverrides: {
      root: {
        display: "flex",
        justifyContent: "flex-end",
        "&.RaToolbar-mobileToolbar": {
          position: "relative !important",
          padding: "0 !important",
        },
      },
    },
  },

  MuiTooltip: {
    defaultProps: {
      placement: "right",
      disableInteractive: true,
    },
    styleOverrides: {
      tooltip: ({ theme }: { theme: Theme }) => ({
        backgroundColor: theme.palette.bg.main,
        border: "1px solid #bdbdbd",
        color: "rgba(0, 0, 0, 0.87)",
        fontSize: 14,
        fontWeight: 400,
        padding: "0.5rem",
        boxShadow: "0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)",
      }),
      arrow: {
        color: "#9e9e9e",
      },
    },
  },
};

// React Admin ships a hard minWidth: theme.spacing(20) on some inputs, which overflows
// columns narrower than that. Keep the floor, but never exceed the available width.
const inputMinWidth = { styleOverrides: { root: { minWidth: "min(160px, 100%)" } } };

const raComponents = {
  RaSelectInput: inputMinWidth,
  RaSelectArrayInput: inputMinWidth,
  RaTextArrayInput: inputMinWidth,
  RaNullableBooleanInput: inputMinWidth,
  RaAutocompleteInput: {
    styleOverrides: {
      root: {
        "& .RaAutocompleteInput-textField": { minWidth: "min(160px, 100%)" },
      },
    },
  },
  RaArrayInput: {
    styleOverrides: {
      // React Admin indents a nested ArrayInput by two spacings. Our nested one (the
      // price positions of an order item) aligns with the inputs around it instead.
      root: {
        "& .RaArrayInput-root": { paddingLeft: 0 },
      },
    },
  },
  RaBulkActionsToolbar: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        "& .RaBulkActionsToolbar-toolbar": {
          backgroundColor: "rgb(204, 213, 237)",
          position: "relative",
          transform: "none",
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
        },
        ".RaBulkActionsToolbar-toolbar.MuiToolbar-root": {
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
        },
        "& .RaBulkActionsToolbar-topToolbar": {
          alignItems: "center",
          paddingBottom: theme.spacing(0.5),
        },
        "& .RaBulkActionsToolbar-title": {
          "& .RaBulkActionsToolbar-icon": {
            backgroundColor: "transparent",
            "&:hover": {
              backgroundColor: "rgba(0,47,167,0.04)",
            },
          },
          "& h6": {
            display: "flex",
            alignItems: "center",
          },
        },
        // All Buttons in BulkActionsToolbar
        "& .MuiButtonBase-root": {
          backgroundColor: "transparent",
          color: theme.palette.primary.main,
          "&:hover": {
            backgroundColor: "rgba(0,47,167,0.04)",
          },
        },
        "& .Mui-disabled": {
          color: theme.palette.text.disabled,
        },
      }),
    },
  },
  RaDatagrid: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        padding: `${theme.spacing(3)} ${theme.spacing(2)}`,
      }),
    },
  },
  RaDataTable: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        padding: `${theme.spacing(3)} ${theme.spacing(2)}`,
      }),
    },
  },
  RaFilterButton: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        backgroundColor: theme.palette.info.main,
        borderRadius: "4px",
        "&:hover": {
          backgroundColor: theme.palette.info.dark,
        },
      }),
    },
  },
  RaFilterForm: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        // flexWrap: "nowrap",
        alignItems: "center",
        marginBottom: theme.spacing(1),

        "& .RaFilterForm-filterFormInput": {
          alignItems: "center",
        },
      }),
    },
  },
  RaFilterFormInput: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        "& .RaFilterFormInput-hideButton": {
          marginBottom: 0,
          marginTop: theme.spacing(1),
          marginLeft: theme.spacing(0.5),
        },
      }),
    },
  },
  RaLoadingIndicator: {
    styleOverrides: {
      root: {
        "& button": {
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: "rgba(0,47,167,0.04)",
          },
        },
      },
    },
  },
  RaRichTextInput: {
    styleOverrides: {
      root: {
        alignItems: "flex-start",

        "& .RaRichTextInput-editorContent div.ProseMirror": {
          minHeight: "100px",
          backgroundColor: "#fff",

          "&:hover, &:focus": {
            backgroundColor: "#fff",
          },
        },
      },
    },
  },
  RaList: {
    styleOverrides: {
      root: {
        "& .RaList-actions": {
          alignItems: "center",
          backgroundColor: "transparent",

          "& .MuiToolbar-root": {
            marginBottom: 0,
            backgroundColor: "transparent",
          },
        },
      },
    },
  },
};

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xxl: true;
  }

  interface Palette {
    bg: Palette["primary"];
    menu: Palette["primary"];
    disabled: Palette["primary"];
    font: Palette["primary"];
  }

  interface PaletteOptions {
    bg: PaletteOptions["primary"];
    disabled: PaletteOptions["primary"];
    font: PaletteOptions["primary"];
  }
}

const edmThemeProps = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 800,
      lg: 1200,
      xl: 1536,
      xxl: 1860,
    },
  },
  palette: {
    primary: {
      main: primaryColor,
      light: "rgba(0,47,167,0.5)",
      dark: "rgb(0, 32, 116)",
    },
    font: {
      dark: "rgba(0, 0, 0, 0.87)",
    },
    info: {
      main: "rgb(241, 243, 247)",
      dark: "rgb(224, 228, 240)",
    },
    secondary: {
      main: "#d3043b",
    },
    error: {
      main: "#d3043b",
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
    bg: {
      light: "rgba(0, 47, 167, 0.04)",
      main: "#fafafa",
      dark: "#f2f2f2",
      portal: "rgb(250, 250, 250)",
    },
    menu: {
      main: "#eee",
    },
    disabled: {
      main: "#cccccc",
      dark: "#6c6c6c",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", "Ubuntu", sans-serif',
    h1: {
      fontSize: "2.75rem",
      fontWeight: 400,
      "@media (min-width:600px)": { fontSize: "3.5rem" },
      "@media (min-width:800px)": { fontSize: "4.25rem" },
      "@media (min-width:1200px)": { fontSize: "5rem" },
    },
    h2: {
      fontSize: "2.5rem",
      fontWeight: 500,
      "@media (min-width:600px)": { fontSize: "3rem" },
      "@media (min-width:800px)": { fontSize: "3.375rem" },
      "@media (min-width:1200px)": { fontSize: "3.75rem" },
    },
    h3: {
      fontSize: "2rem",
      fontWeight: 500,
      "@media (min-width:600px)": { fontSize: "2.5rem" },
      "@media (min-width:800px)": { fontSize: "2.75rem" },
      "@media (min-width:1200px)": { fontSize: "3rem" },
    },
    h4: {
      fontSize: "1.375rem",
      "@media (min-width:600px)": { fontSize: "1.425rem" },
      "@media (min-width:800px)": { fontSize: "1.5rem" },
      "@media (min-width:1200px)": { fontSize: "1.75rem" },
    },
    h5: {
      fontSize: "1.175rem",
      "@media (min-width:600px)": { fontSize: "1.25rem" },
      "@media (min-width:800px)": { fontSize: "1.375rem" },
      "@media (min-width:1200px)": { fontSize: "1.5rem" },
    },
    h6: {
      fontSize: "1.125rem",
      fontWeight: 500,
      "@media (min-width:600px)": { fontSize: "1.15rem" },
      "@media (min-width:800px)": { fontSize: "1.2rem" },
      "@media (min-width:1200px)": { fontSize: "1.25rem" },
    },
    fontWeightBold: 700,
    fontWeightMedium: 500,
    fontWeightRegular: 400,
  },
  sidebar: {
    width: 225,
    closedWidth: 60,
  },
  components: {
    ...muiComponents,
    ...raComponents,
  },
};

export const mergedTheme = deepmerge(defaultTheme, edmThemeProps);

// @TODO [TS] Fix why MuiAppBar.styleOverrides.root.position is not expected as string.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default createTheme(mergedTheme);
